import SideControlsPage from "Layouts/SideControls/SideControlsPage";
import { MAIN_APP_BAR_HEIGHT_PX } from "components/Navigation/appBarHeight";
import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import SignalsPageStore from "./Store/SignalsPageStore";
import SignalsSidebar from "./Sidebar/SignalsSidebar";
import {
    useLocation,
    useNavigate,
    useNavigationType,
    useParams,
} from "react-router";
import DashboardBody from "./Dashboard/DashboardBody";
import SignalsCustomReportBody from "./Reports/SignalsCustomReportBody";
import { isReportsLocation } from "./SignalsUtils";
import ChartCardStore from "./BaseObjects/ChartCard/ChartCardStore";
import HomeInsightsCards from "components/Signals/Dashboard/HomeInsightsCards";
import { AuthStore } from "stores/AuthStore";
import { SignalsDashboardType } from "./SignalsModels";
import { getCanonicalSignalsLocation } from "./SignalsRouting";

// dashboard routing test cases. invalid should redirect to home dashboard and update url
// /app/signals - valid (goes to home)
// /app/signals/home - valid
// /app/signals/eddy - valid
// /app/signals/eddy/invalid-id - invalid
// /app/signals/dashboard/valid-id - valid
// /app/signals/dashboard/invalid-id - invalid
// /app/signals/discover - valid (goes to default home)
// /app/signals/discover/invalid-id - valid (unique case, this will go to default home and replace url to just /discover)
// /app/signals/invalid-route - invalid
// /app/signals/invalid-route/invalid-id - invalid
// Also try home related routes having set
// - personal home
// - org home
// - no home (should go to default home which is drivers or eddy depending on permissions)
// - both personal and org home (personal should take precedence)

// This is the main layout for Signals. drawerContent is the sidebar and mainContent is the body of the page
// The useEffect on first load checks the URL params to see what dashboard to load
function SignalsLayout() {
    const store = useStore(SignalsPageStore);
    const authStore = useStore(AuthStore);
    const navigate = useNavigate();
    const location = useLocation();
    const { dashboardRoute, dashboardId } = useParams<{
        dashboardRoute?: string;
        dashboardId?: string;
    }>();

    const dashboardsLoading = store.getTaskLoading("LoadDashboards");
    const navigationType = useNavigationType();

    const isHomeDashboard =
        store.currentDashboard?.isUserHomeDashboard ||
        store.currentDashboard?.isOrgHomeDashboard;

    const hasNoHomeDashboard =
        !store.userHomeDashboard && !store.orgHomeDashboard;

    const isDefaultThemesHomeDashboard =
        hasNoHomeDashboard &&
        store.currentDashboard?.dashboardType ===
            SignalsDashboardType.ThemesAndDrivers;

    const shouldShowOnDashboard =
        isHomeDashboard || isDefaultThemesHomeDashboard;

    const isDiscoverSignalsPage =
        location.pathname === "/app/signals/discover";

    const showHomeInsights =
        authStore.canUserView("Proactive Insights") &&
        (isDiscoverSignalsPage ||
            (shouldShowOnDashboard && !isReportsLocation(location)));
    const homeInsightsLoading = store.getTaskLoading("LoadHomeInsights");

    useEffect(() => {
        if (!showHomeInsights) {
            return;
        }

        store.loadHomeInsights();
    }, [showHomeInsights, store]);

    // On first load, redirect legacy Topics and Reports URLs to their supported routes.
    useEffect(() => {
        const canonicalLocation = getCanonicalSignalsLocation({
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
        });

        if (!canonicalLocation) {
            return;
        }

        navigate(canonicalLocation, { replace: true });
    }, [location.hash, location.pathname, location.search, navigate]);

    // On first load, set the current dashboard based on the URL params
    useEffect(() => {
        // SIGNALSTODO - Canceling dashboard fetches when forced away from signals OR leaving signals in general
        if (store.authStore.canUserView("Signals") === false) {
            navigate(`/app/myevaluations`, { replace: true });
            // cancel task?
            return;
        }

        if (getCanonicalSignalsLocation(location)) {
            return;
        }

        if (
            store.getTaskLoading("LoadDashboards") ||
            (isReportsLocation(location) &&
                store.authStore.canUserView("Reporting"))
        ) {
            // If we're still loading dashboards, or if we're on reporting and can view it, don't do anything
            // Reporting has its own handling, so we don't need to set current dashboard here
            return;
        }

        store.setCurrentDashboard({
            dashboardRoute,
            dashboardId,
            navigationType,
            navigate,
            locationPathname: location.pathname,
        });

        // We only want this to run on these deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardId, dashboardRoute, dashboardsLoading]);

    const currentDashboard = store.currentDashboard;
    const redrawRenderedCharts = () => {
        if (!currentDashboard) return;
        currentDashboard.charts.forEach((objStore) => {
            const chartStore = objStore as unknown as ChartCardStore;
            setTimeout(() => {
                if (!chartStore.chartWrapper) return;
                chartStore.chartWrapper?.draw();
            }, 100); // timeout is to ensure the page has rendered before re-drawing
        });
    };

    return (
        <SideControlsPage
            mainContent={
                isReportsLocation(location) ? (
                    <SignalsCustomReportBody hideTitle={true} />
                ) : (
                    <>
                        {showHomeInsights && (
                            <HomeInsightsCards
                                insights={store.homeInsights}
                                isLoading={homeInsightsLoading}
                            />
                        )}

                        <DashboardBody />
                    </>
                )
            }
            containerOffsetPixels={MAIN_APP_BAR_HEIGHT_PX}
            drawerMaxWidth={300}
            drawerContent={<SignalsSidebar />}
            drawerLocation="left"
            mainGridAlignItems="stretch"
            mainGridJustify="flex-start"
            mainGridSpacing={0}
            mainSx={{ padding: 0 }}
            drawerClosed={store.currentDashboard?.isPresentationMode}
            enableMinimize
            onDrawerChange={redrawRenderedCharts}
            hideMinimizeToggle={store.currentDashboard?.isPresentationMode}
        />
    );
}

export default observer(SignalsLayout);
